// File: ../../assets/pilotsidebar.jsx (or wherever you keep it)

import React from 'react';
import { useUser } from '@clerk/clerk-react';
import {
  LayoutDashboard, Bot, Wrench, BookOpen, Database, Server, Activity,
  DollarSign, FlaskConical, Link, Cpu, HelpCircle
} from 'lucide-react';

// --- Helper Components (SidebarNavItem, SidebarNavGroup) ---
// (Keep these exactly as they were in your provided code)
const SidebarNavItem = ({ icon: Icon, text, href, active, onClick }) => (
    <a
      href={href}
      onClick={onClick} // Use the passed onClick handler
      className={`flex items-center pl-3 pr-2 py-1.5 rounded-md text-xs font-medium transition-colors duration-150 group relative ${
        active ? 'bg-gray-800/60 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
      }`}
    >
      {active && <div className="absolute left-0 top-1 bottom-1 w-0.5 bg-purple-500 rounded-r-md"></div>}
      <Icon
        className={`w-4 h-4 mr-2.5 flex-shrink-0 ${
          active ? 'text-purple-400' : 'text-gray-500 group-hover:text-gray-300'
        }`}
        strokeWidth={1.5}
      />
      <span className="truncate">{text}</span>
    </a>
  );

  const SidebarNavGroup = ({ children }) => (
    <div className="mt-3">
      <nav className="space-y-0.5 px-2">{children}</nav>
    </div>
  );
// --- End Helper Components ---

// --- Pidebar Component ---
const Pidebar = ({ activeItem = 'Dashboard', setActiveItem }) => {
  const { user } = useUser();

  const navItems = [
        {
      items: [
        // Use a relative path or full path if needed, '#' won't navigate meaningfully
        { text: 'Dashboard', icon: LayoutDashboard, href: '/' }, // Changed href
      ]
    },
    {
      items: [ // Core Creation/Management
        { text: 'Create agent', icon: Bot, href: '/echidna/copilot' },
        { text: 'Tools', icon: Wrench, href: '/echidna/efoundry/tools' },
         // Use actual paths, not just # unless handled by routing/scrolling
        { text: 'Knowledge', icon: BookOpen, href: 'https://metabees.org/foundry-docs', target: '_blank' },
        { text: 'Datasets', icon: Database, href: '/echidna/efoundry/data' },
      ]
    },
    {
      items: [ // Operations & Monitoring
        { text: 'Deployments', icon: Server, href: '/echidna/efoundry/deployments' },
        { text: 'Monitoring', icon: Activity, href: '/echidna/foundry/agentanalytics' },
        { text: 'Cost Explorer', icon: DollarSign, href: '/echidna/foundry/stats' },
      ]
    },
    {
      items: [ // Configuration & Support
        { text: 'Playground', icon: FlaskConical, href: '/echidna/efoundry/playground' },
        { text: 'Simulations', icon: Link, href: '/echidna/efoundry/simulations' },
        { text: 'Models', icon: Cpu, href: '/echidna/efoundry/models' },
        { text: 'Help', icon: HelpCircle, href: '/echidna/efoundry/help' }, // Or a link to docs
      ]
    },
  ];

  // Handle navigation click: ONLY update state, let browser navigate
  const handleNavClick = (e, itemText, href) => {
    // --- REMOVE OR COMMENT OUT e.preventDefault() ---
    // e.preventDefault(); // Stop the redirect ONLY IF using client-side routing (like React Router)

    // Update the visual state (highlight)
    if (typeof setActiveItem === 'function') {
      setActiveItem(itemText); // Call the function passed via props
      console.log(`Sidebar: Set active item state to: ${itemText}`);
    } else {
      console.error("Pidebar Error: setActiveItem prop is missing or is not a function.");
    }

    // --- LET THE BROWSER HANDLE NAVIGATION ---
    // No need for navigate(href) here if we're allowing the default link behavior
    console.log(`Sidebar: Allowing browser to navigate to: ${href}`);
  };

  const userInitial = user?.firstName?.charAt(0) || user?.lastName?.charAt(0) || 'U';
  const userDisplayName = user?.firstName
    ? `${user.firstName}${user.lastName ? ` ${user.lastName.charAt(0)}.` : ''}`
    : user?.primaryEmailAddress?.emailAddress || 'User';

  return (
    <div className="w-52 md:w-56 h-screen bg-black border-r border-gray-800/50 flex flex-col flex-shrink-0 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700/50 scrollbar-track-black">
      {/* Logo/Header */}
      <div className="h-14 flex items-center px-3 flex-shrink-0 border-b border-gray-800/50">
         {/* ... logo svg ... */}
         <div className="flex items-center">
          <svg className="w-6 h-6 mr-1.5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2L2 7L12 12L22 7L12 2Z" fill="#8B5CF6" stroke="#8B5CF6" strokeWidth="1.5"/>
            <path d="M2 17L12 22L22 17" stroke="#8B5CF6" strokeWidth="1.5"/>
            <path d="M2 12L12 17L22 12" stroke="#8B5CF6" strokeWidth="1.5"/>
          </svg>
          <span className="text-base font-bold text-white">Echidna Foundry</span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex-grow py-2">
        {navItems.map((group, index) => (
          <SidebarNavGroup key={`group-${index}`}>
            {group.items.map(item => (
              <SidebarNavItem
                key={item.text}
                icon={item.icon}
                text={item.text}
                href={item.href}
                active={activeItem === item.text}
                // Pass the modified click handler
                onClick={(e) => handleNavClick(e, item.text, item.href)}
              />
            ))}
          </SidebarNavGroup>
        ))}
      </div>

      {/* User Area */}
      <div className="p-2 border-t border-gray-800/50 mt-auto">
         {/* ... user display anchor tag ... */}
         <a href="#profile" onClick={(e) => e.preventDefault()} className="flex items-center p-2 rounded-md text-xs text-gray-400 hover:bg-gray-800/50 hover:text-white group">
          <span className="w-5 h-5 rounded-full bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-[10px] font-bold text-white mr-2 group-hover:opacity-90 uppercase flex-shrink-0">
            {userInitial}
          </span>
          <span className="truncate font-medium">{userDisplayName}</span>
        </a>
      </div>
    </div>
  );
};

export default Pidebar;